/**
 * Moving between cards with the keyboard (the PDApps window) and reordering the
 * Favorites deck — the pure parts, so they are unit-tested.
 */

export type NavKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Home' | 'End'
export const NAV_KEYS: readonly string[] = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']

export interface Box { left: number; top: number; width: number; height: number }

/**
 * The card to go to from card `from`, given every card's box in page order: Left / Right
 * step through the page in order; Up / Down go to the nearest card (by its centre) in
 * the row above / below — the next section's first row too; Home / End to the first /
 * last card. -1: nowhere to go.
 */
export function nextCardIndex(boxes: readonly Box[], from: number, key: NavKey): number {
  if (from < 0 || from >= boxes.length) return -1
  if (key === 'Home') return 0
  if (key === 'End') return boxes.length - 1
  if (key === 'ArrowLeft') return from > 0 ? from - 1 : -1
  if (key === 'ArrowRight') return from < boxes.length - 1 ? from + 1 : -1
  const cur = boxes[from]
  const cx = cur.left + cur.width / 2
  const down = key === 'ArrowDown'
  // The nearest row in that direction: the smallest gap between the rows' tops.
  let rowTop: number | null = null
  for (const b of boxes) {
    const ahead = down ? b.top > cur.top + cur.height / 2 : b.top < cur.top - cur.height / 2
    if (!ahead) continue
    if (rowTop === null || (down ? b.top < rowTop : b.top > rowTop)) rowTop = b.top
  }
  if (rowTop === null) return -1
  let best = -1
  let bestDx = Infinity
  boxes.forEach((b, i) => {
    if (Math.abs(b.top - rowTop!) > b.height / 2) return
    const dx = Math.abs(b.left + b.width / 2 - cx)
    if (dx < bestDx) { bestDx = dx; best = i }
  })
  return best
}

/** `list` with the item at `from` moved to `to` (a new array). */
export function moveItem<T>(list: readonly T[], from: number, to: number): T[] {
  const out = [...list]
  if (from < 0 || from >= out.length || to < 0 || to >= out.length || from === to) return out
  const [item] = out.splice(from, 1)
  out.splice(to, 0, item)
  return out
}

/**
 * The slot under the point (x, y), from the slots' boxes — or, between slots, the
 * nearest one by its centre. The slots are the grid's places, which don't move while a
 * card is dragged (only what is in them does), so the answer never jitters.
 */
export function slotAt(slots: readonly Box[], x: number, y: number): number {
  let best = -1
  let bestD = Infinity
  slots.forEach((s, i) => {
    if (x >= s.left && x <= s.left + s.width && y >= s.top && y <= s.top + s.height) { best = i; bestD = -1; return }
    if (bestD < 0) return
    const d = Math.hypot(s.left + s.width / 2 - x, s.top + s.height / 2 - y)
    if (d < bestD) { bestD = d; best = i }
  })
  return best
}

/** How many cards a row holds, from the slots' boxes (the ones sharing the first top). */
export function columnsOf(slots: readonly Box[]): number {
  if (slots.length === 0) return 1
  const top = slots[0].top
  return Math.max(1, slots.filter(s => Math.abs(s.top - top) < s.height / 2).length)
}

/**
 * The deck in the person's order: `ids` (the server's order — oldest favorite first)
 * sorted by where each one's key sits in `stored`; favorites the order doesn't know yet
 * go after, in the server's order.
 */
export function orderDeck<T>(items: readonly T[], keyOf: (item: T) => string, stored: readonly string[] | null): T[] {
  if (!stored || stored.length === 0) return [...items]
  const at = new Map(stored.map((k, i) => [k, i]))
  return items
    .map((item, i) => ({ item, i, pos: at.get(keyOf(item)) }))
    .sort((a, b) => {
      if (a.pos !== undefined && b.pos !== undefined) return a.pos - b.pos
      if (a.pos !== undefined) return -1
      if (b.pos !== undefined) return 1
      return a.i - b.i
    })
    .map(x => x.item)
}
